/**
 * Admin — community issue moderation via the shared ModerationScreen
 * pattern: category + status, one-tap status PATCH (optimistic, reverting
 * on error). Wired: GET/PATCH /api/admin/issues.
 * Port of screens-admin.jsx AdminIssues.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { num, str, useGateList } from '@/components/gate/api';
import { ModerationScreen, SmallButton } from '@/features/account-admin/bits';
import { CatPill } from '@/features/community/bits';
import { api } from '@/lib/client';
import { AppText, Card, Pill } from '@/ui';

type Status = 'open' | 'progress' | 'resolved';
type Row = { id: string; title: string; cat: string; status: Status; up: number; comments: number };

const STATUSES: Array<[Status, string]> = [['open', 'Open'], ['progress', 'In progress'], ['resolved', 'Resolved']];

function statusOf(v: string): Status {
  const s = v.toLowerCase().replace(/[\s-]/g, '_');
  if (s === 'in_progress' || s === 'progress') return 'progress';
  if (s === 'resolved' || s === 'closed' || s === 'done') return 'resolved';
  return 'open';
}

function toRow(it: unknown, i: number): Row {
  return {
    id: str(it, 'id', 'code') ?? `i${i}`,
    title: str(it, 'title', 'subject') ?? 'Issue',
    cat: str(it, 'cat', 'category') ?? 'General',
    status: statusOf(str(it, 'status') ?? 'open'),
    up: num(it, 'up', 'upvotes', 'upvoteCount') ?? 0,
    comments: num(it, 'comments', 'commentCount') ?? 0,
  };
}

export default function AdminIssuesScreen() {
  const { items, setItems, state } = useGateList<unknown>('/api/admin/issues', 'issues');
  const rows = items.map(toRow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const setStatus = (id: string, status: Status) => {
    const prev = items;
    setNotice(null);
    setItems(prev.map((it, i) => (toRow(it, i).id === id ? { ...(it as object), status } : it))); // optimistic
    api.patch<unknown>(`/api/admin/issues/${id}`, { status }).catch(() => {
      setItems(prev); // revert
      setNotice('Could not update the issue status. Try again.');
    });
  };

  return (
    <ModerationScreen
      title="Community issues"
      state={state}
      count={rows.length}
      emptyIcon="alert-circle"
      emptyTitle="No community issues"
      emptyBody="Issues residents raise for the whole society will appear here for triage."
      notice={notice}
    >
      {rows.map((it) => (
        <Card key={it.id}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s2 }}>
            <CatPill label={it.cat} />
            <Pill status={it.status} />
          </View>
          <AppText variant="bodyMd" weight="500" style={{ marginTop: space.s2 }}>{it.title}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s1 }}>
            {it.up} upvotes · {it.comments} comments
          </AppText>
          <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3 }}>
            {STATUSES.map(([value, label]) => (
              <SmallButton
                key={value}
                label={label}
                active={it.status === value}
                onPress={() => { if (it.status !== value) setStatus(it.id, value); }}
              />
            ))}
          </View>
        </Card>
      ))}
    </ModerationScreen>
  );
}
